"use client";

import { FormProvider, SubmitHandler, useForm, useFormContext } from "react-hook-form";
import { ContactFormValues, contactSchema } from "@/Types/schemas/contact/ContactFormTypes";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { handleFormError } from "@/utils/dashboardForms/editAnyForm/handleFormError";
import toast from "react-hot-toast";
import api from "@/libs/axios";



function FormFields({isSubmitting}: {
    isSubmitting: boolean
}) {

    const { register, control, formState: {errors} } = useFormContext<ContactFormValues>();

    return(
        <div className="flex flex-col w-full space-y-4">

            {/* firstName and lastName */}
            <div className="flex w-full gap-2 md:gap-4">

                {/* first name */}
                <div className="flex w-1/2">
                    <input 
                        type="text" 
                        id="firstName"
                        placeholder="First Name*"
                        {...register("firstName")}
                        className="w-full bg-white placeholder:text-black text-black text-sm px-2 md:px-8 py-1 md:py-2 border border-primary"
                    />
                    {errors.firstName?.message && typeof errors.firstName?.message === "string" && (
                        <p className="text-red-500 text-sm">{errors.firstName.message}</p>
                    )}
                </div>

                {/* last name */}
                <div className="flex w-1/2">
                    <input
                        type="text"
                        id="lastName"
                        placeholder="Last name*"
                        {...register("lastName")}
                        className="w-full bg-white placeholder:text-black text-black text-sm px-2 md:px-8 py-1 md:py-2 border border-primary"
                    />
                    {errors.lastName?.message && typeof errors.lastName?.message === "string" && (
                        <p className="text-red-500 text-sm">{errors.lastName.message}</p>
                    )}
                </div>
            </div>
            
            {/* email and phone */}
            <div className="flex w-full gap-2 md:gap-4">
                {/* email */}
                <div className="flex w-1/2">
                    <input 
                        type="email" 
                        id="userEmail" 
                        placeholder="Email*"
                        {...register("userEmail")}
                        className="w-full bg-white placeholder:text-black text-black text-sm px-2 md:px-8 py-1 md:py-2 border border-primary"
                    />
                    {errors.userEmail?.message && typeof errors.userEmail?.message === "string" && (
                        <p className="text-red-500 text-sm">
                            {errors.userEmail.message}
                        </p>
                    )}
                </div>
                {/* phone */}
                <div className="flex w-1/2">
                    <input 
                        type="tel" 
                        id="userPhone" 
                        placeholder="Phone*"
                        {...register("userPhone")}
                         className="w-full bg-white placeholder:text-black text-black text-sm px-2 md:px-8 py-1 md:py-2 border border-primary"
                    />
                    {errors.userPhone?.message && typeof errors.userPhone?.message === "string" && (
                        <p className="text-red-500 text-sm">
                            {errors.userPhone.message}
                        </p>
                    )}
                </div>
            </div>

            {/* textarea */}
            <div className="flex w-full md:gap-4">
                <div className="flex w-full">
                    <textarea 
                        id="additionalInfo"
                        rows={4}
                        {...register("additionalInfo")}
                        placeholder="Additional Information*"
                        className="w-full bg-white placeholder:text-black text-black text-sm px-2 md:px-8 py-1 md:py-2 border border-primary"
                    />
                    {errors.additionalInfo?.message && typeof errors.additionalInfo?.message === "string" && (
                        <p className="text-red-500 text-sm">
                            {errors.additionalInfo.message}
                        </p>
                    )}
                </div>
            </div>

            {/* submit */}
            <button className="self-start bg-accent text-black font-medium px-6 py-3">
                Get in-touch
            </button>

        </div>

    );
}


export default function ContactForm() {

    const [ isSubmitting, setIsSubmitting ] = useState(false);

    const methods = useForm<ContactFormValues>({
        resolver: zodResolver(contactSchema),
        defaultValues: {
            firstName: "",
            lastName: "",
            userEmail: "",
            userPhone: "",
            additionalInfo: ""
            
        }
    });

    const onSubmit: SubmitHandler<ContactFormValues> = async (data) => {

        try{

            setIsSubmitting(true);

            const formData = new FormData();
            
            // fields
            formData.append("firstName", data.firstName);
            formData.append("lastName", data.lastName);
            formData.append("userEmail", data.userEmail);
            formData.append("userPhone", data.userPhone);
            formData.append("additionalInfo", data.additionalInfo);

            // collect form data
            // for(const [k, v] of formData.entries()){
            //     console.log(k, v);
            // }

            // submit collected data
            await api.post("/api/contact", formData, {
                headers: { "Content-Type": "multipart/form-data"},
            })

            //clean form fields on successful submission
            methods.reset();

            //toast success notification
            toast.success("Successfully submitted form");

        } catch(error: any) {

            console.error("Submit error:", error);
            console.error("Validation errors:", error.response?.data);
            handleFormError(error, methods.setError, new Set(Object.keys(methods.getValues())));

        } finally {
            
            setIsSubmitting(false);

        }
    }
    return (
        <div className="flex flex-col w-full">
            <FormProvider {...methods}>
                <form 
                    onSubmit={methods.handleSubmit(onSubmit)}
                    noValidate
                    className="flex flex-col w-full space-y-2"
                >
                    <FormFields
                        isSubmitting={isSubmitting}
                    />
                </form>
            </FormProvider>
        </div>
    );
}